import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getProgrammeFullTitles } from '../../utils/programmeDisplay';
import {
  displayValue,
  getRequirementExplanation,
  hasProgrammeBenchmarkScores,
  ProgrammeBenchmarkScores,
  ProgrammeMetaList,
  hasScoreCalculationDetail,
  ProgrammeScoreCalculationDetailTable,
  ProgrammeStatsGrid,
} from '../../utils/jupasProgrammeDisplay';
import {
  ClassificationUnavailableExplanation,
  canViewJupasErrorCodes,
  getClassificationBadgeDisplay,
  getClassificationLoadingBadgeDisplay,
  getClassificationNoticeMessage,
  isClassificationUnavailable,
  JupasPanelStatusLabel,
  JupasRequirementCheckerBadge,
  isRequirementFulfillmentAwaiting,
  resolveRequirementBadgeValue,
  resolveRequirementFulfillmentDisplay,
} from '../../utils/jupasClassificationDisplay';
import CollapsibleSlide from '../ui/CollapsibleSlide';
import { JUPAS_SIX_ROW_LIST_MIN_HEIGHT_CLASS } from '../../utils/jupasLayoutDisplay';

const getCollectionYear = (collection) => {
  const match = String(collection || '').match(/\d{4}$/);
  return match ? Number(match[0]) : null;
};

function ProgrammeDetailsEmptyState({ t }) {
  return (
    <div className={`flex flex-col items-center justify-center rounded-lg border border-dashed border-edge bg-surface-muted/40 px-6 py-10 text-center ${JUPAS_SIX_ROW_LIST_MIN_HEIGHT_CLASS}`}>
      <p className="text-sm font-semibold text-ink">{t('jupa.programmeDetailsEmptyTitle')}</p>
      <p className="mt-2 max-w-xs text-sm leading-relaxed text-ink-subtle">
        {t('jupa.selectProgrammeForDetails')}
      </p>
    </div>
  );
}

function DetailSection({ children, className = '' }) {
  return (
    <section className={`px-3 py-3.5 sm:px-5 ${className}`}>
      {children}
    </section>
  );
}

function DetailSectionHeading({ children }) {
  return (
    <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-subtle sm:text-sm sm:font-medium sm:normal-case sm:tracking-normal sm:text-ink-muted">
      {children}
    </p>
  );
}

function ProgrammeNameBlock({ programme }) {
  const { en, cn } = getProgrammeFullTitles(programme);
  const showCn = cn && cn !== en;

  return (
    <div className="space-y-2">
      {programme.code ? (
        <span className="inline-flex rounded-md border border-sky-100 bg-sky-50 px-2 py-1 text-[11px] font-semibold leading-none text-[#185FA5] dark:border-sky-800/60 dark:bg-sky-950/50 dark:text-sky-300 sm:text-xs">
          {programme.code}
        </span>
      ) : null}
      <div className="space-y-1">
        {en ? (
          <p className="text-[15px] font-semibold leading-snug text-ink sm:text-sm">{en}</p>
        ) : null}
        {showCn ? (
          <p className="text-sm leading-snug text-ink-muted">{cn}</p>
        ) : null}
        {!en && cn ? (
          <p className="text-sm font-semibold leading-snug text-ink">{cn}</p>
        ) : null}
      </div>
    </div>
  );
}

const DETAIL_SUPPORTING_TEXT_CLASS = 'text-sm leading-relaxed text-ink-subtle';

function DetailLabelTextRow({ label, children, className = '' }) {
  return (
    <div className={`grid min-w-0 grid-cols-1 items-start gap-y-2 rounded-lg border border-edge/70 bg-surface px-3 py-3 sm:grid-cols-[6.75rem_minmax(0,1fr)] sm:items-center sm:gap-x-3 sm:gap-y-0 sm:border-0 sm:bg-transparent sm:p-0 ${className}`}>
      <div className="flex min-w-0 items-center justify-start sm:w-[6.75rem] sm:justify-center">
        {label}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function RequirementBlock({
  fulfillmentDisplay,
  requirementFulfillment,
  classificationLoading,
  t,
}) {
  const explanation = classificationLoading
    ? t('common.loading')
    : requirementFulfillment
      ? getRequirementExplanation(requirementFulfillment, t)
      : null;

  if (!fulfillmentDisplay && !classificationLoading) {
    return null;
  }

  return (
    <DetailLabelTextRow
      label={fulfillmentDisplay ? (
        <div className="inline-flex min-w-0 items-center justify-start gap-1.5 sm:w-full sm:justify-center">
          <JupasRequirementCheckerBadge display={fulfillmentDisplay} />
          {fulfillmentDisplay.englishLabel ? (
            <span className="text-xs font-semibold leading-none text-ink sm:text-sm">{fulfillmentDisplay.englishLabel}</span>
          ) : null}
        </div>
      ) : null}
    >
      {explanation ? (
        <p className={DETAIL_SUPPORTING_TEXT_CLASS}>{explanation}</p>
      ) : null}
    </DetailLabelTextRow>
  );
}

function ScoreClassificationBlock({
  programme,
  classificationData,
  classificationLoading,
  badgeDisplay,
  showErrorCode,
  t,
}) {
  const classificationError = classificationData?.classification_error;
  const resolvedClassification = classificationData?.classification;
  const classificationUnavailable = isClassificationUnavailable(resolvedClassification);
  const classificationNoticeMessage = getClassificationNoticeMessage(
    classificationData?.classification_notice,
    t
  );
  const userScore = classificationData?.user_score;
  const upperQuartileScore = programme?.score_uq ?? classificationData?.score_uq;
  const medianScore = programme?.score_md ?? classificationData?.score_md;
  const lowerQuartileScore = programme?.score_lq ?? classificationData?.score_lq;
  const hasUserScore = userScore !== null
    && userScore !== undefined
    && Number.isFinite(Number(userScore));
  const hasBenchmarks = hasProgrammeBenchmarkScores(
    upperQuartileScore,
    medianScore,
    lowerQuartileScore
  );

  if (classificationLoading) {
    return (
      <DetailLabelTextRow
        label={<JupasPanelStatusLabel display={badgeDisplay} />}
      >
        <p className={DETAIL_SUPPORTING_TEXT_CLASS}>{t('common.loading')}</p>
      </DetailLabelTextRow>
    );
  }

  if (classificationUnavailable || classificationError) {
    return (
      <DetailLabelTextRow
        label={<JupasPanelStatusLabel display={badgeDisplay} />}
      >
        <ClassificationUnavailableExplanation
          error={classificationError}
          classification={resolvedClassification}
          showErrorCode={showErrorCode}
          t={t}
        />
      </DetailLabelTextRow>
    );
  }

  const showScore = hasUserScore || hasBenchmarks || classificationNoticeMessage;
  if (!showScore && !badgeDisplay) {
    return null;
  }

  return (
    <DetailLabelTextRow
      label={<JupasPanelStatusLabel display={badgeDisplay} />}
    >
      <div className="space-y-2">
        {hasUserScore ? (
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold tabular-nums leading-none text-ink">
              {displayValue(userScore)}
            </span>
            <span className={DETAIL_SUPPORTING_TEXT_CLASS}>{t('jupa.yourCalculatedScore')}</span>
          </div>
        ) : null}
        {hasBenchmarks ? (
          <ProgrammeBenchmarkScores
            upperQuartileScore={upperQuartileScore}
            medianScore={medianScore}
            lowerQuartileScore={lowerQuartileScore}
            labelClassName={`${DETAIL_SUPPORTING_TEXT_CLASS} font-medium`}
            valueClassName={`${DETAIL_SUPPORTING_TEXT_CLASS} font-semibold tabular-nums text-ink`}
          />
        ) : !hasUserScore ? (
          <p className={DETAIL_SUPPORTING_TEXT_CLASS}>{t('jupa.scoreBenchmarkUnavailable')}</p>
        ) : null}
        {classificationNoticeMessage ? (
          <p className="rounded-md bg-amber-50 px-2.5 py-2 text-xs leading-relaxed text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            {classificationNoticeMessage}
          </p>
        ) : null}
      </div>
    </DetailLabelTextRow>
  );
}

export default function ProgrammeDetailsPanel({
  programme,
  selectedCollection,
  classificationData,
  classificationLoading = false,
}) {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const showErrorCode = canViewJupasErrorCodes(currentUser);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const year = getCollectionYear(selectedCollection);

  useEffect(() => {
    setDescriptionExpanded(false);
  }, [programme?.code]);

  if (!programme) {
    return <ProgrammeDetailsEmptyState t={t} />;
  }

  const resolvedClassification = classificationData?.classification ?? programme.classification;
  const classificationPending = classificationLoading || resolvedClassification == null;
  const badgeDisplay = classificationPending
    ? getClassificationLoadingBadgeDisplay(t)
    : getClassificationBadgeDisplay(resolvedClassification, t);
  const fulfillmentDisplay = resolveRequirementFulfillmentDisplay(
    resolveRequirementBadgeValue(classificationData),
    t,
    {
      loading: classificationLoading,
      awaiting: isRequirementFulfillmentAwaiting(classificationData, { classificationLoading }),
    }
  );
  const requirementFulfillment = classificationLoading
    ? null
    : classificationData?.requirement_fulfillment;
  const hasMeta = Boolean(
    programme.duration?.trim()
    || programme.interview
    || programme.funding?.trim()
    || programme.institution
  );
  const showScoreBlock = classificationLoading
    || classificationData?.user_score != null
    || classificationData?.classification_error
    || classificationData?.classification_notice
    || isClassificationUnavailable(classificationData?.classification)
    || programme.score_uq != null
    || programme.score_md != null
    || programme.score_lq != null
    || badgeDisplay;
  const showCalcDetail = classificationLoading
    || hasScoreCalculationDetail(classificationData, t);

  return (
    <div className={`flex min-w-0 flex-col overflow-hidden rounded-xl border border-edge bg-surface shadow-sm ${JUPAS_SIX_ROW_LIST_MIN_HEIGHT_CLASS}`}>
      <DetailSection className="shrink-0 space-y-3 bg-primary/5 pb-4 pt-4 sm:bg-surface-muted/30">
        <ProgrammeNameBlock programme={programme} />
        <RequirementBlock
          fulfillmentDisplay={fulfillmentDisplay}
          requirementFulfillment={requirementFulfillment}
          classificationLoading={classificationLoading}
          t={t}
        />
        {showScoreBlock ? (
          <ScoreClassificationBlock
            programme={programme}
            classificationData={classificationData}
            classificationLoading={classificationLoading}
            badgeDisplay={badgeDisplay}
            showErrorCode={showErrorCode}
            t={t}
          />
        ) : null}
      </DetailSection>

      <div>
        {showCalcDetail ? (
          <DetailSection className="border-t border-edge/70">
            <DetailSectionHeading>{t('jupa.scoreCalculationDetailHeading')}</DetailSectionHeading>
            <ProgrammeScoreCalculationDetailTable
              classificationData={classificationData}
              classificationLoading={classificationLoading}
              t={t}
            />
          </DetailSection>
        ) : null}

        <DetailSection className="border-t border-edge/70">
          <DetailSectionHeading>{t('jupa.admissionStatistics')}</DetailSectionHeading>
          <ProgrammeStatsGrid
            programme={programme}
            classificationData={classificationLoading ? undefined : classificationData}
            t={t}
            year={year}
            includeRequirementRow={false}
            compact
            borderless
          />
        </DetailSection>

        {hasMeta ? (
          <DetailSection className="border-t border-edge/70">
            <DetailSectionHeading>{t('jupa.programmeDetailsMetaHeading')}</DetailSectionHeading>
            <ProgrammeMetaList programme={programme} t={t} language={i18n.language} compact />
          </DetailSection>
        ) : null}

        {programme.description ? (
          <DetailSection className="border-t border-edge/70 pb-4 pt-3.5">
            <button
              type="button"
              onClick={() => setDescriptionExpanded((prev) => !prev)}
              aria-expanded={descriptionExpanded}
              className="flex min-h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-lg px-1 text-left text-sm font-semibold text-ink transition-colors hover:text-link focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-canvas sm:min-h-0 sm:font-medium"
            >
              <span>
                {descriptionExpanded ? t('jupa.collapseProgrammeDetails') : t('jupa.programmeDescription')}
              </span>
              <svg
                className={`h-5 w-5 shrink-0 transition-transform duration-200 ease-out ${descriptionExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <CollapsibleSlide open={descriptionExpanded}>
              <p className="mt-2 max-h-56 overflow-y-auto whitespace-pre-line rounded-lg bg-surface-muted/50 px-3 py-2.5 text-sm leading-relaxed text-ink-muted sm:max-h-48 sm:bg-transparent sm:p-0">
                {programme.description}
              </p>
            </CollapsibleSlide>
          </DetailSection>
        ) : null}
      </div>
    </div>
  );
}
